import React, { useEffect, useContext } from 'react'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useTenant } from '@emerald-works-nova/auth'
import { Divider } from '@mui/material'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Container, VideoPlayer, GoogleAd } from '../'
import { contentUrl } from '../../helpers/url'
import { useAlert, useGoogleAdsense, useFormFactor, usePlaylistNavigation, useGroup } from '../../hooks'
import { contentSlice } from '../../reducers'
import { useAddToBookmarks } from '../add-to-bookmarks'
import { ArticleBodyLoading } from '../article-view/article-view-loading'
import CommentsView from '../comments-view'
import Hide from '../hide'
import { useSEO } from '../seo-context'
import CallToActionMeteringUsage from './cta-metering'
import ContentMetaTags from './meta-tags'
import Paywall from './preview-paywall'
import DiscoverMoreContent from './discover-more-content'
import { useStyles } from './style'
import { PromotedContentGenericCard } from '../general-card/promoted-content-card'
import { useXapi } from '../../hooks/xapi'
import RelatedArticles from './related-articles'
import { GoogleAdContext } from '../google-ad/google-ad-context'
import { ALL_NON_PAYING } from '../../services/cognito/userGroups'
import { STATIC_AD_SLOTS } from '../../consts'

const Body = React.lazy(() => import('./body'))

const Article = props => {
  const { isInGroup } = useGroup()
  const isNonPayingUser = isInGroup(ALL_NON_PAYING)
  const { sendAdsRequest, adsRequestSent } = useContext(GoogleAdContext)

  const tenant = useTenant()
  const hasCommentsField = Object.hasOwn(tenant.features, 'comments')
  const { comments } = tenant.features
  const commentsSectionActive = hasCommentsField ? comments : true

  const content = useSelector(contentSlice.selectors.selectContent)
  const discoverMoreContent = useSelector(contentSlice.selectors.selectDiscoverMoreContent)
  const relatedContent = useSelector(contentSlice.selectors.selectRelatedContent)
  const isPreview = useSelector(contentSlice.selectors.selectIsPreview)
  const showMeteringCallToAction = useSelector(contentSlice.selectors.selectShowMeternigCallToAction)
  const [getRelatedContent] = useEvent([
    {
      eventName: 'contentGetRelatedContent',
      onSuccess: [{ redux: true, action: contentSlice.actions.setDiscoverMoreContent }, { redux: true, action: contentSlice.actions.setRelatedContent }],
      onError: () => {
        enqueueMsg('Unable to load related content recommendation', 'error')
      }
    }
  ])

  const navigate = useNavigate()
  const { width, minInset, onAddToPlaylist } = props
  const {
    videoUrl,
    locationId
  } = content

  const classes = useStyles(props)
  const { enqueueMsg } = useAlert()
  const handleAddToPlaylist = article => onAddToPlaylist(article)

  const { isBookmarked, handleBookmark } = useAddToBookmarks({ from: 'article' })

  const handleArticleSelect = article => {
    navigate(contentUrl(article, 'discover-more-content'))
  }

  const { useAdsense, shouldDisplayContentStaticAds } = useGoogleAdsense()

  useEventsOnViewLoad(() => {
    if (locationId && !getRelatedContent.hasBeenTriggered) {
      getRelatedContent.trigger({
        locationId
      })
    }
  }, [locationId, getRelatedContent])

  useSEO(!!discoverMoreContent && !!relatedContent && !getRelatedContent.isWorking)

  useXapi(content)

  const { isDesktop, isLargeDesktop } = useFormFactor()
  const { playlistContextId } = usePlaylistNavigation()

  useEffect(() => {
    if (discoverMoreContent.length > 0 && isNonPayingUser && !adsRequestSent) {
      sendAdsRequest()
    }
  }, [discoverMoreContent, isNonPayingUser, adsRequestSent, sendAdsRequest])

  return (
    <>
      <ContentMetaTags content={content} />
      <Container
        maxContentWidth={width}
        inset={minInset}
        classes={{ content: classes.article }}
        className={classes.container}
      >
        <VideoPlayer url={videoUrl} />
        {/* Content Header Ad - Only Displayed on Articles */}
        {(useAdsense && shouldDisplayContentStaticAds(content, STATIC_AD_SLOTS.HEADER)) &&
          <GoogleAd
            adKey='content'
            adPosition='header'
            adSource='components>article>index.js'
          />}
        <React.Suspense fallback={<ArticleBodyLoading />}>
          <Body
            body={content?.body}
            className={classes.body}
          />
        </React.Suspense>
        {/* Render paywall content for SEO */}
        {content.paywall && (
          <Hide hidden={isPreview} className='paywall'>
            <React.Suspense fallback={<ArticleBodyLoading />}>
              <Body body={content.paywall} />
            </React.Suspense>
          </Hide>
        )}
        {!isPreview && (
          <>
            {showMeteringCallToAction
              ? <CallToActionMeteringUsage />
              : <Divider className={classes.divider} />}
            {/* <Cite onClick={handleCite} /> */}
            <DiscoverMoreContent
              title='Discover more content'
              renderArticle={article => (
                <PromotedContentGenericCard
                  content={article}
                  onSelect={() => handleArticleSelect(article)}
                  onAddToPlaylist={() => handleAddToPlaylist(article)}
                  onBookmark={() => handleBookmark(article)}
                  bookmarked={isBookmarked(article)}
                />
              )}
            />
            {commentsSectionActive && <CommentsView locationId={locationId} />}
          </>
        )}
        {!(isDesktop || isLargeDesktop) && !playlistContextId && <RelatedArticles title='Related Articles' />}
      </Container>
      {isPreview && (
        <Paywall />
      )}
    </>
  )
}

export default Article
